// src/components/timeline/TimelineCard.tsx
"use client";

import Markdown from "@/components/Markdown";
import { TimelinePost } from "@/lib/timeline";
import TimelineCollage from "./TimelineCollage";
import TimelineReactionBar from "./TimelineReactionBar";
import TimelineComments from "./TimelineComments";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import CollectionBadge from "@/components/CollectionBadge";
import { useCollectionModal } from "@/hooks/useCollectionModal";
import TagPills from "@/components/TagPills";
import dynamic from "next/dynamic";
import type { Collection } from "@/types/timeline";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";

// Dynamic import for modal to avoid hydration issues
const CollectionModal = dynamic(() => import("@/components/CollectionModal"), {
  ssr: false,
});

type Props = {
  projectId: number;
  post: TimelinePost;
  initialReactionSummary?: any;
  collectionSlug?: string;
  mediaType?: string;
};

export default function TimelineCard({
  projectId,
  post,
  initialReactionSummary,
  collectionSlug,
  mediaType,
}: Props) {
  const { formatDate } = useDateFormatter();

  // Tag modal hook
  const { selectedCollection, isOpen, openModal, closeModal } =
    useCollectionModal();
  const t = useTranslations("post");
  const locale = useLocale();
  // Format number based on locale
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(locale).format(num);
  };
  // Handle tag click - uses the full tag object from the post
  const handleCollectionClick = (collection: Collection) => {
    openModal(collection);
  };

  // Get the first image/video for thumbnail if needed
  const hasMedia = post.files && post.files.length > 0;
  const firstFile = hasMedia ? post.files[0]?.file : null;
  const isVideo = firstFile?.media_kind === "video";
  const isAudio = firstFile?.media_kind === "audio";

  // Helper function to get the appropriate label with number
  const getOrderLabel = () => {
    if (!collectionSlug && !mediaType && post.project_order > 0) {
      const formattedNumber = formatNumber(post.project_order);
      return t("post_with_number", { number: formattedNumber });
    }
    if ((collectionSlug || mediaType) && post.collection_order > 0) {
      const formattedNumber = formatNumber(post.collection_order);
      return t("episode_with_number", { number: formattedNumber });
    }
    return null;
  };

  const orderLabel = getOrderLabel();
  const tags = post.tags ?? [];
  return (
    <>
      <div
        style={{
          marginBottom: "2rem",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          paddingBottom: "1.5rem",
        }}
      >
        {/* Post Header - Date and metadata */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.75rem",
            fontSize: "0.875rem",
            opacity: 0.7,
          }}
        >
          <time dateTime={post.created}>
            {formatDate(new Date(post.created))}
          </time>
          {/* Display the order label if it exists */}
          {orderLabel && <span>{orderLabel}</span>}
        </div>

        {/* Media Files */}
        {hasMedia && (
          <div style={{ marginBottom: "1rem" }}>
            <TimelineCollage items={post.files} />
          </div>
        )}

        {/* Caption / Content */}
        {post.caption && (
          <div style={{ marginBottom: "1rem" }}>
            <Markdown content={post.caption} />
          </div>
        )}

        {/* ============================================ */}
        {/* COLLECTION SECTION - COMPLETE IMPLEMENTATION */}
        {/* ============================================ */}

        {/* Show the main community collection (primary collection) */}
        {post.collection && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              marginTop: "0.75rem",
              marginBottom: "0.75rem",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "0.75rem",
                opacity: 0.5,
                marginRight: "0.25rem",
              }}
            >
              {t("in_collection")}
            </span>
            <CollectionBadge
              collection={post.collection}
              onClick={handleCollectionClick}
              size="medium"
              showIcon={true}
            />
          </div>
        )}

        {tags.length > 0 && (
          <div style={{ marginTop: "0.5rem", marginBottom: "0.75rem" }}>
            <TagPills tags={tags} />
          </div>
        )}

        {/* Action Bar - Only show if likes are not hidden */}
        {!post.likes_hidden && (
          <TimelineReactionBar
            projectId={projectId}
            postId={post.id}
            initialSummary={initialReactionSummary}
          />
        )}

        {/* Comments Section */}
        {!post.comments_disabled && (
          <TimelineComments
            projectId={projectId}
            postId={post.id}
            disabled={false}
          />
        )}
      </div>

      {/* Tag Concentration Modal */}
      <CollectionModal
        isOpen={isOpen}
        collection={selectedCollection}
        projectId={projectId}
        onClose={closeModal}
        onCopyPermalink={(url) => {
          console.log("Permalink copied:", url);
          // You can add a toast notification here
        }}
        onShare={(tag) => {
          console.log("Shared tag:", tag);
        }}
        onAnalytics={(eventName, payload) => {
          // Send to your analytics service
          console.log(`[Analytics] ${eventName}:`, payload);
          // Example: if (typeof window !== 'undefined' && window.gtag) {
          //   window.gtag('event', eventName, payload);
          // }
        }}
      />
    </>
  );
}
